// =============================================================================
// useApiKeys Hook
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import { useLogCollectorClient } from './useLogCollectorClient.js';
import {
  ApiClientError,
  type ApiKey,
  type ApiKeyWithKey,
  type ApiKeyPermission,
} from '../lib/api.js';

// =============================================================================
// Types
// =============================================================================

export interface UseApiKeysOptions {
  /** Base URL override */
  baseUrl?: string;
  /** Filter by app ID */
  appId?: string;
  /** Whether to fetch API keys on mount (default: true) */
  fetchOnMount?: boolean;
}

export interface UseApiKeysResult {
  /** List of API keys */
  apiKeys: ApiKey[];
  /** Whether API keys are being loaded */
  isLoading: boolean;
  /** Error from the last operation */
  error: string | null;
  /** Fetch/refresh API keys list */
  fetchApiKeys: () => Promise<void>;
  /** Create a new API key */
  createApiKey: (
    appId: string,
    permissions?: ApiKeyPermission,
    rateLimit?: number
  ) => Promise<ApiKeyWithKey | null>;
  /** Delete an API key */
  deleteApiKey: (keyId: string) => Promise<boolean>;
  /** Clear error state */
  clearError: () => void;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for managing API keys.
 *
 * @example
 * ```tsx
 * function ApiKeysList({ appId }: { appId: string }) {
 *   const {
 *     apiKeys,
 *     isLoading,
 *     error,
 *     createApiKey,
 *     deleteApiKey,
 *     fetchApiKeys,
 *   } = useApiKeys({ appId });
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error} />;
 *
 *   const handleCreate = async () => {
 *     const key = await createApiKey(appId, 'write', 1000);
 *     if (key) {
 *       // Show the full key to user (only available at creation time)
 *       alert(`New API Key: ${key.key}\nSave this - it won't be shown again!`);
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleCreate}>Create API Key</button>
 *       <button onClick={fetchApiKeys}>Refresh</button>
 *       <ul>
 *         {apiKeys.map(key => (
 *           <li key={key.id}>
 *             {key.key_prefix}... ({key.permissions})
 *             <button onClick={() => deleteApiKey(key.id)}>Delete</button>
 *           </li>
 *         ))}
 *       </ul>
 *     </div>
 *   );
 * }
 * ```
 */
export function useApiKeys(options: UseApiKeysOptions = {}): UseApiKeysResult {
  const { baseUrl, appId, fetchOnMount = true } = options;
  const { client } = useLogCollectorClient({ baseUrl });

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch API keys list.
   */
  const fetchApiKeys = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await client.listApiKeys(appId);
      setApiKeys(result);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to fetch API keys');
      }
    } finally {
      setIsLoading(false);
    }
  }, [client, appId]);

  /**
   * Create a new API key.
   */
  const createApiKey = useCallback(
    async (
      targetAppId: string,
      permissions: ApiKeyPermission = 'write',
      rateLimit = 1000
    ): Promise<ApiKeyWithKey | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const key = await client.createApiKey(targetAppId, permissions, rateLimit);
        // Add the key to the list (without the full key value)
        const { key: _fullKey, ...keyWithoutFullKey } = key;
        setApiKeys((prev) => [keyWithoutFullKey, ...prev]);
        return key;
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to create API key');
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [client]
  );

  /**
   * Delete an API key.
   */
  const deleteApiKey = useCallback(
    async (keyId: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        await client.deleteApiKey(keyId);
        setApiKeys((prev) => prev.filter((key) => key.id !== keyId));
        return true;
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to delete API key');
        }
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [client]
  );

  /**
   * Clear error state.
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Fetch on mount if enabled
  useEffect(() => {
    if (fetchOnMount) {
      fetchApiKeys();
    }
  }, [fetchOnMount, fetchApiKeys]);

  return {
    apiKeys,
    isLoading,
    error,
    fetchApiKeys,
    createApiKey,
    deleteApiKey,
    clearError,
  };
}
